import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const AuthContext = createContext();

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(() => localStorage.getItem('auth_token'));
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchUser = useCallback(async () => {
    if (!token) {
      setLoading(false);
      return;
    }

    try {
      const response = await axios.get(`${API_URL}/api/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUser(response.data);
      setError(null);
    } catch (err) {
      console.error('Failed to fetch user:', err);
      setToken(null);
      setUser(null);
      localStorage.removeItem('auth_token');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchUser();
  }, [fetchUser]);

  const sendMagicLink = async (email) => {
    try {
      setError(null);
      const response = await axios.post(`${API_URL}/api/auth/magic-link`, { email });
      return response.data;
    } catch (err) {
      const message = err.response?.data?.detail || 'Failed to send magic link';
      setError(message);
      throw new Error(message);
    }
  };

  const verifyMagicLink = async (verifyToken) => {
    try {
      setError(null);
      const response = await axios.post(`${API_URL}/api/auth/verify`, { token: verifyToken });
      const { token: authToken, user: userData } = response.data;
      
      setToken(authToken);
      setUser(userData);
      localStorage.setItem('auth_token', authToken);
      
      return userData;
    } catch (err) {
      const message = err.response?.data?.detail || 'Invalid or expired link';
      setError(message);
      throw new Error(message);
    }
  };

  const updateProfile = async (updates) => {
    if (!token) throw new Error('Not authenticated');

    try {
      const response = await axios.put(
        `${API_URL}/api/auth/profile`,
        updates,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setUser(response.data);
      return response.data;
    } catch (err) {
      const message = err.response?.data?.detail || 'Failed to update profile';
      throw new Error(message);
    }
  };

  const logout = () => {
    setToken(null);
    setUser(null);
    localStorage.removeItem('auth_token');
  };

  const value = {
    user,
    token,
    loading,
    error,
    isAuthenticated: !!user,
    sendMagicLink,
    verifyMagicLink,
    updateProfile,
    logout,
    refreshUser: fetchUser,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
